
/* eslint-disable dot-notation */

import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import TaskDetailsTitle from '@/components/TaskDetailsTitle.vue';
import TaskC from '@/components/TaskC.vue';
import TaskP from '@/components/TaskP.vue';
import TaskO from '@/components/TaskO.vue';
import TaskCm from '@/components/TaskCm.vue';
import TaskS from '@/components/TaskS.vue';
import TaskA from '@/components/TaskA.vue';
import TaskV from '@/components/TaskV.vue';
import TaskM from '@/components/TaskM.vue';
import TaskI from '@/components/TaskI.vue';
import TaskR from '@/components/TaskR.vue';
import TaskActions from '@/components/TaskActions.vue';
import CloseTaskDialog from '@/components/CloseTaskDialog.vue';
import UpdateTaskDialog from '@/components/UpdateTaskDialog.vue';
import SelectNetworkDialog from '@/components/SelectNetworkDialog.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import {
  Lead, LeadStatus, TaskStatus, Task, TaskName, Network, ConfirmDialogOptions, BoxStatusType,
} from '@/types';
import {
  canEditTaskStatus, getLeadCustomValueText, getLeadCustomValueDate, isBlank,
} from '@/helpers';
import { leadRouteCode } from '@/helpers/leadCode';

@Component({
  components: {
    TaskDetailsTitle,
    TaskC,
    TaskP,
    TaskO,
    TaskCm,
    TaskS,
    TaskA,
    TaskV,
    TaskM,
    TaskI,
    TaskR,
    TaskActions,
    CloseTaskDialog,
    UpdateTaskDialog,
    SelectNetworkDialog,
    ConfirmDialog,
  },
})
export default class EditTaskDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop({ type: Boolean, default: false }) readonly left!: boolean;

  dialog = false
  updateTaskDialog = false
  closeTaskDialog = false
  networkDialog = false

  confirmDialog = false
  confirmOptions: ConfirmDialogOptions = {
    title: 'Attenzione',
    text: 'Stai pianificando una spedizione finale, ma il materiale non è completamente in magazzino.',
    question: 'Vuoi modificare la disponibilità materiale impostandola su "In magazzino"?',
    yesText: 'Sì, modificala',
    noText: 'No, lasciala com\'è',
  }
  confirmCallback: (() => void) | null = null

  refTask: Vue | Element | Vue[] | Element[] | null = null

  @Watch('value')
  async onValueChange(val: boolean) {
    console.log(`${this.constructor.name}: onValueChange(${val})`); // eslint-disable-line no-console

    this.dialog = val;

    if (val === false) {
      // Deseleziona il Task alla chiusura del dialog
      this.$store.dispatch('unselectTask');

      const id = this.$route.params.fatherId;
      if (id) {
        this.$router.push({ name: 'Lead', params: { code: leadRouteCode(id) } });
      }
    } else {
      // Memorizza il timestamp di apertura del dialog
      this.$store.dispatch('openDialogTimestamp');
    }
  }

  // Quando il Task ha finito di caricare, se è stato specificato un focusField
  // (es. cliccando una cella specifica in Operations.vue), scorre automaticamente
  // al blocco `<div data-field="...">` corrispondente all'interno del TaskX.
  @Watch('loading')
  async onLoadingChange(val: boolean, oldVal: boolean) {
    if (oldVal === true && val === false) {
      await this.$nextTick();
      await this.$nextTick();
      this.scrollToFocusField();
    }
  }

  scrollToFocusField() {
    const field: string | null = this.$store.getters.selectedTaskFocusField;
    if (!field) return;

    const taskRef = this.$refs.task as Vue | undefined;
    const root = taskRef?.$el as HTMLElement | undefined;
    if (!root) return;

    const target = root.querySelector(`[data-field="${field}"]`);
    if (target && typeof (target as HTMLElement).scrollIntoView === 'function') {
      (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  // La Lead selezionata memorizzata tramite Vuex
  get selectedLead(): Lead {
    return this.$store.getters.currentLead;
  }

  // Il Task selezionato memorizzato tramite Vuex
  get selectedTask(): Task | null {
    return this.$store.getters.selectedTask;
  }

  get networks(): Network[] { return this.$store.getters.networks; }

  // Sto caricando il Task
  get loading(): boolean {
    return this.$store.getters.isTaskLoading;
  }

  // Sto aggiornando il Task
  get updating(): boolean {
    return this.$store.getters.isTaskUpdating;
  }

  // È necessario salvare le modifiche al Task?
  get dirty(): boolean {
    return this.$store.getters.isTaskDirty;
  }

  get valid(): boolean {
    return this.$store.getters.isTaskValid;
  }

  get taskName(): TaskName | null {
    return this.selectedTask?.name as TaskName ?? null;
  }

  get leadStatus(): LeadStatus {
    return this.selectedLead?.status;
  }

  get taskStatus(): TaskStatus {
    return this.selectedTask?.status ?? null;
  }

  get canEditTaskStatus(): boolean { return canEditTaskStatus(); }

  async updateWithStatus(status: 'completed' | 'error', force = false) {
    console.log(`${this.constructor.name}: updateWithStatus(${status})`); // eslint-disable-line no-console

    // Se il modulo è valido o l'aggiornamento è forzato...
    if (this.valid || force) {
      // ... aggiunge le informazioni sullo stato all'aggiornamento
      this.$store.dispatch('updateTaskFields', { status });

      // e procede con l'aggiornamento del Task
      await this.$store.dispatch('updateSelectedTask');

      this.dialog = false;
    }
  }

  get currentFather(): Lead | null {
    return this.$store.getters.currentFather;
  }

  get payments() {
    return this.currentFather?.payments ?? 0;
  }

  get loan() {
    return this.currentFather ? getLeadCustomValueText(this.currentFather, 'loan') : null;
  }

  get pod() {
    return this.currentFather ? getLeadCustomValueText(this.currentFather, 'pod') : null;
  }

  get checkZonaVincolo() {
    return this.currentFather ? getLeadCustomValueText(this.currentFather, 'checkZonaVincolo') : null;
  }

  get autodichiarazioneCliente() {
    return this.currentFather ? getLeadCustomValueText(this.currentFather, 'autodichiarazioneCliente') : null;
  }

  get checkAccountGSE() {
    return this.currentFather
      ? (getLeadCustomValueText(this.currentFather, 'checkAccountGSE') ?? 'todo') as BoxStatusType
      : null;
  }

  get usernameGSE() {
    return this.currentFather ? getLeadCustomValueText(this.currentFather, 'usernameGSE') : null;
  }

  get passwordGSE() {
    return this.currentFather ? getLeadCustomValueText(this.currentFather, 'passwordGSE') : null;
  }

  get checkContoEnergia() {
    return this.currentFather
      ? getLeadCustomValueText(this.currentFather, 'checkContoEnergia') ?? 'todo'
      : null;
  }

  get orderConfirmationDate() {
    return this.currentFather ? getLeadCustomValueDate(this.currentFather, 'orderConfirmationDate') : null;
  }

  get selectedTaskUpdate(): { [key: string]: never } {
    return this.$store.getters.selectedTaskUpdate;
  }

  // Chiede eventualmente conferma prima di aggiornare il Task
  extraConfirmationNeededBeforeUpdate(): boolean {
    if (this.selectedTask?.name === 'M') {
      // Se non è attivo un finanziamento e lo stato del pagamento è inferiore a €€
      if (this.loan !== 'yes' && this.payments < 2 && this.payments !== -1
        && (
          // ... e si sta modificando la data obiettivo del materiale in cantiere
          ('eMaterialsTargetDate' in this.selectedTaskUpdate && this.selectedTaskUpdate['eMaterialsTargetDate'] !== null)
          || ('tMaterialsTargetDate' in this.selectedTaskUpdate && this.selectedTaskUpdate['tMaterialsTargetDate'] !== null)
          // ... oppure si sta pianificando una spedizione
          || ('partialShipment' in this.selectedTaskUpdate && this.selectedTaskUpdate['partialShipment'] === true)
          || ('partialShipmentDate' in this.selectedTaskUpdate && this.selectedTaskUpdate['partialShipmentDate'] !== null)
          || ('shipmentReady' in this.selectedTaskUpdate && this.selectedTaskUpdate['shipmentReady'] === true)
          || ('deliveryDate' in this.selectedTaskUpdate && this.selectedTaskUpdate['deliveryDate'] !== null)
        )
      ) {
        // Ferma il salvataggio e chiede prima conferma
        console.log(`${this.constructor.name}: extraConfirmationNeededBeforeUpdate() -> true`); // eslint-disable-line no-console

        // Personalizza il messaggio
        this.confirmOptions.text = 'Stai pianificando il cantiere e/o la spedizione del materiale di questa commessa MA non è stato incassato l\'acconto (€€).';
        this.confirmOptions.question = 'Vuoi comunque salvare le modifiche e renderle effettive?';
        this.confirmOptions.yesText = 'Si, salva le modifiche';
        this.confirmOptions.noText = 'No, annulla';

        return true;
      }
    }

    if (this.selectedTask?.name === 'R') {
      // Rimuove gli spazi e rende tutto minuscolo il POD
      const fixedPod = this.pod ? this.pod.replace(/ /g, '').toLowerCase() : '';

      // Condizioni (AND):
      // - Il POD non deve iniziare con IT001E
      // - Lo stato dell'allaccio è su un "Data concordata"
      // - Lo stato dell'allaccio è su "Allaccio completato"
      if (!fixedPod.startsWith('it001e')
        && 'egridConnectionStatus' in this.selectedTaskUpdate
        && (
          this.selectedTaskUpdate['egridConnectionStatus'] === 'not-confirmed'
          || this.selectedTaskUpdate['egridConnectionStatus'] === 'no-confirmation-required'
          || this.selectedTaskUpdate['egridConnectionStatus'] === 'connected'
        )
      ) {
        // Ferma il salvataggio e chiede prima conferma
        console.log(`${this.constructor.name}: extraConfirmationNeededBeforeUpdate() -> true`); // eslint-disable-line no-console

        // Personalizza il messaggio
        this.confirmOptions.text = 'Ricordarsi di richiedere al cliente e/o all\'installatore di trasmetterci la scansione del verbale di allaccio';
        this.confirmOptions.question = 'Vuoi comunque salvare le modifiche e renderle effettive?';
        this.confirmOptions.yesText = 'Si, salva le modifiche';
        this.confirmOptions.noText = 'No, annulla';

        return true;
      }
    }

    console.log(`${this.constructor.name}: extraConfirmationNeededBeforeUpdate() -> false`); // eslint-disable-line no-console
    return false;
  }

  // Chiede eventualmente conferma prima di completare il Task
  extraConfirmationNeededBeforeComplete(): boolean {
    if (this.selectedTask?.name === 'Cm') {
      const pod = 'pod' in this.selectedTaskUpdate ? this.selectedTaskUpdate['pod'] : this.pod;
      const confirmOptionsStringsArray = [''];
      const fixedPod = pod !== null ? pod.replace(/ /g, '').toLowerCase() || '' : '';

      const checkZonaVincolo = 'checkZonaVincolo' in this.selectedTaskUpdate ? this.selectedTaskUpdate['checkZonaVincolo'] : this.checkZonaVincolo;
      const autodichiarazioneCliente = 'autodichiarazioneCliente' in this.selectedTaskUpdate ? this.selectedTaskUpdate['autodichiarazioneCliente'] : this.autodichiarazioneCliente;

      const checkZonaVincoloAndAutodichiarazioneClienteBoxStatusesAreBothNAOrDone = (checkZonaVincolo === 'na' && autodichiarazioneCliente === 'na')
        || (checkZonaVincolo === 'done' && autodichiarazioneCliente === 'done');

      const checkAccountGSE = 'checkAccountGSE' in this.selectedTaskUpdate ? this.selectedTaskUpdate['checkAccountGSE'] : this.checkAccountGSE;
      const usernameGSE = 'usernameGSE' in this.selectedTaskUpdate ? this.selectedTaskUpdate['usernameGSE'] : this.usernameGSE;
      const passwordGSE = 'passwordGSE' in this.selectedTaskUpdate ? this.selectedTaskUpdate['passwordGSE'] : this.passwordGSE;
      const checkContoEnergia = 'checkContoEnergia' in this.selectedTaskUpdate ? this.selectedTaskUpdate['checkContoEnergia'] : this.checkContoEnergia;

      const checkAccountGSENotValidParameters = checkAccountGSE !== 'na'
        && (!usernameGSE || !passwordGSE || checkContoEnergia === 'todo');

      // Verifica data conferma d'ordine
      const orderConfirmationDate = 'orderConfirmationDate' in this.selectedTaskUpdate ? this.selectedTaskUpdate['orderConfirmationDate'] : this.orderConfirmationDate;

      // Verifiche di validità per il POD. Niente piu' IBAN Caparra: il campo non
      // e' piu' inseribile (OwlCaveLabs/syndesia-l2c-support#6 e #30).
      if (isBlank(pod) || fixedPod?.startsWith('it002e')
        || !checkZonaVincoloAndAutodichiarazioneClienteBoxStatusesAreBothNAOrDone
        || checkAccountGSENotValidParameters
        || !orderConfirmationDate
      ) {
        console.log(`${this.constructor.name}: extraConfirmationNeededBeforeComplete() -> true`); // eslint-disable-line no-console

        // Personalizza il messaggio
        if (isBlank(pod)) {
          confirmOptionsStringsArray.push('Stai completando il Task senza aver inserito un codice POD.');
        }
        if (fixedPod?.startsWith('it002e')) {
          confirmOptionsStringsArray.push('Il codice POD inserito è un POD su rete ARETI/ACEA perciò è necessaria la taratura in campo del relè di interfaccia come da contratto.');
        }

        if (!checkZonaVincoloAndAutodichiarazioneClienteBoxStatusesAreBothNAOrDone) {
          confirmOptionsStringsArray.push('Stai completando il Task senza aver inserito il check zona vincolo e/o l\'autodichiarazione del cliente.');
        }

        if (checkAccountGSENotValidParameters) {
          confirmOptionsStringsArray.push('Stai completando il Task col Check Account GSE diverso da \'Non Applicabile\' e senza aver completato tutti i parametri correttamente.');
        }

        if (!orderConfirmationDate) {
          confirmOptionsStringsArray.push('Stai completando il Task senza aver inserito la data conferma d\'ordine. Se non inserita, verrà impostata automaticamente la data di completamento del Task (oggi).');
        }

        this.confirmOptions.text = confirmOptionsStringsArray;
        this.confirmOptions.question = 'Vuoi procedere?';
        this.confirmOptions.yesText = 'Si, completalo';
        this.confirmOptions.noText = 'No, annulla';

        return true;
      }
    }

    console.log(`${this.constructor.name}: extraConfirmationNeededBeforeComplete() -> false`); // eslint-disable-line no-console
    return false;
  }

  // Effetta la validazione del modulo (nel Task)
  async onValidate(status: 'completed' | 'error') {
    console.log(`${this.constructor.name}: onValidate(${status})`); // eslint-disable-line no-console

    // Recupero il Task utilizzando il riferimento in $refs
    this.refTask = this.$refs.task as Vue;

    const task = this.refTask as Vue & {
      onValidate: (status: 'completed' | 'error') => Promise<void>;
    };

    // Avvia l'effettiva validazione (torna una Promise)
    return task.onValidate(status);
  }

  async onComplete(force = false) {
    console.log(`${this.constructor.name}: onComplete()`); // eslint-disable-line no-console

    // Valido il modulo con le nuove informazioni inserite
    await this.onValidate('completed');

    setTimeout(() => {
      console.log('EditTaskDialog: onComplete() this.valid =', this.valid); // eslint-disable-line no-console

      // Se il modulo è valido o se è stato forzato il completamento...
      if (this.valid || force) {
        // Effettua verifiche aggiuntive che potrebbero richiedere di mostrare un alert
        if (this.extraConfirmationNeededBeforeComplete()) {
          // Prepara la callback da chiamare se l'utente conferma
          this.confirmCallback = () => {
            // Aggiorna il Task con lo stato 'completed'
            this.updateWithStatus('completed', force);
          };

          // Mostra il dialog di conferma prima di completare il Task
          this.confirmDialog = true;
          return;
        }
      }

      this.updateWithStatus('completed', force);
    }, 250);
  }

  async onError() {
    console.log(`${this.constructor.name}: onError()`); // eslint-disable-line no-console

    // Valido il modulo con le nuove informazioni inserite
    await this.onValidate('error');

    // eslint-disable-next-line no-unreachable
    setTimeout(() => {
      console.log('EditTaskDialog: onError() this.valid =', this.valid); // eslint-disable-line no-console

      this.updateWithStatus('error');
    }, 250);
  }

  async onClose() {
    console.log(`${this.constructor.name}: onClose()`); // eslint-disable-line no-console

    // Se è "dirty", prima di chiudere chiede di salvare i dati
    if (this.dirty) {
      this.updateTaskDialog = true;
      return;
    }

    // Se l'utente può modificare lo stato del Task (per completarlo ad es.)...
    if (this.canEditTaskStatus) {
      // ... Verifica la validità dei dati inseriti
      await this.onValidate('completed');

      setTimeout(() => {
        // Se il Task è in lavorazione ma le informazioni contenute sono valide...
        if (this.taskStatus === null && this.valid) {
          // ... propone il completamento del Task
          this.closeTaskDialog = true;
        } else {
          this.dialog = false;
        }
      }, 250);
    } else {
      this.dialog = false;
    }
  }

  async onUpdate(close = false) {
    console.log(`${this.constructor.name}: onUpdate(close = ${close})`); // eslint-disable-line no-console

    // Effettua verifiche aggiuntive che potrebbero richiedere di mostrare un alert
    if (this.extraConfirmationNeededBeforeUpdate()) {
      // Prepara la callback da chiamare se l'utente conferma
      this.confirmCallback = async () => {
        // Aggiorna il task selezionato
        await this.$store.dispatch('updateSelectedTask');

        // Chiude il dialog solo se richiesto
        if (close) this.dialog = false;
      };

      // Mostra il dialog di conferma e ferma l'aggiornamento
      this.confirmDialog = true;
      return;
    }

    // Aggiorna il task selezionato
    await this.$store.dispatch('updateSelectedTask');

    // Chiude il dialog solo se richiesto
    if (close) this.dialog = false;
  }

  onGoto(where: string) {
    console.log('EditTaskDialog: onGoto() where =', where); // eslint-disable-line no-console

    this.dialog = false;

    switch (where) {
      case 'opNotes':
        this.$router.push({
          name: 'Lead',
          params: { code: leadRouteCode(this.selectedLead.id) },
          query: { tab: 'opNotes' },
        });
        break;

      default:
        break;
    }
  }

  async onSuperAction(action: string) {
    console.log(`${this.constructor.name}: onSuperAction('${action}')`); // eslint-disable-line no-console

    switch (action) {
      case 'taska.upgrade_to_v2':
      case 'taskr.upgrade_to_v2':
        // Richiede la conversione del Task
        await this.$store.dispatch('convertTask', {
          task: this.selectedTask,
          version: 2,
        });

        // Chiude il dialog
        this.dialog = false;
        break;

      default:
        break;
    }
  }

  onConfiguratore() {
    console.log(`${this.constructor.name}: onConfiguratore()`); // eslint-disable-line no-console

    // Se manche l'id della lead si ferma subito
    const leadId = this.selectedLead?.id;
    if (!leadId) return;

    if (this.selectedLead?.network) {
      // Se nella lead è già impostata una rete di vendita,
      // utilizza quella.

      window.location.href = `/lead/${leadId}/quotation/new`;
    } else
    if (this.networks.length === 1) {
      // Nella lead non è impostata una rete di vendita
      // ma all'utente è assegnata una sola rete, utilizza quella
      const network = this.networks[0].shortname;

      window.location.href = `/lead/${leadId}/quotation/new?&network=${network}`;
    } else
    if (this.networks.length > 1) {
      // Nella lead non è impostata una rete di vendita
      // e all'utente sono assegnate più reti di vendita.
      // Chiede quale rete utilizzare.

      this.networkDialog = true;
    }
  }

  onNetworkSelect(network) {
    console.log(`${this.constructor.name}: onNetworkSelect()`); // eslint-disable-line no-console

    if (this.selectedLead) {
      window.location.href = `/lead/${this.selectedLead.id}/quotation/new?&network=${network}`;
    }
  }

  async onConfirm() {
    console.log(`${this.constructor.name}: onConfirm()`); // eslint-disable-line no-console

    // Esegue la callback se presente
    if (this.confirmCallback) {
      await this.confirmCallback();
    }
  }
}
